import { useEffect, useState } from 'react';
import { pad, trackPosition } from '../campaign';
import { BadgeCheckIcon, DoneCheckIcon, LockBtnIcon } from './Icons';
import cardArt from '../assets/diwali/card-art.png';
import goldArt from '../assets/diwali/gold-art.png';
import diyaLit from '../assets/diwali/diya-lit.png';
import diya from '../assets/diwali/diya.png';
import chest from '../assets/diwali/chest.png';

const ART = { 'diwali-card': cardArt, diwaliCard: cardArt, 'diwali-gold': goldArt, diwaliGold: goldArt };
const isGold = (key) => key === 'diwali-gold' || key === 'diwaliGold';

/* `lockText` from the API, or the Card / Gold line from the design. */
const lockCopy = (s) => {
  if (s.lockText) return s.lockText;
  const name = /gold/i.test(`${s.requires || ''} ${s.key || ''}`) ? 'Gold' : 'Card';
  return `Complete the ${name} streak to\nunlock this bonus`;
};

const remain = (endsAt) => {
  const end = endsAt instanceof Date ? endsAt.getTime() : NaN;
  let d = (Number.isFinite(end) ? end : 0) - Date.now();
  if (d < 0) d = 0;
  return {
    days: Math.floor(d / 864e5),
    hrs: Math.floor((d % 864e5) / 36e5),
    mins: Math.floor((d % 36e5) / 6e4),
    secs: Math.floor((d % 6e4) / 1e3),
  };
};

function useRemain(endsAt) {
  const [left, setLeft] = useState(() => remain(endsAt));
  useEffect(() => {
    setLeft(remain(endsAt));
    const id = setInterval(() => setLeft(remain(endsAt)), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return left;
}

/* One Diwali card: art, copy, Bonus Chest chip, a ₹0–₹2000 diya track and
   the orange CTA. A diya lights once `amount` reaches that step. `locked`
   (the API's flag) swaps the CTA for the lock note. */
function DiwaliCard({ streak, onCta }) {
  const s = streak;
  const step = Math.max(0, Math.min(s.progress || 0, 4));
  const locked = s.locked === true;
  const pos = locked ? 0 : trackPosition({
    amount: s.amount,
    stepAmount: s.stepAmount,
    steps: s.steps,
    progress: step,
    completed: s.completed,
  });
  const run = (s.steps || 4) * (s.stepAmount || 500);
  const gold = isGold(s.key);

  return (
    <article className={`gq-dw-card${gold ? ' gq-gold' : ''}${s.completed ? ' gq-done' : ''}${locked ? ' gq-locked' : ''}`}>
      <div className="gq-dw-art">
        <img className="gq-dw-artimg" src={ART[s.key] || cardArt} alt="" />
        <img className="gq-dw-artdiya" src={diyaLit} alt="" />
      </div>
      {s.completed && !locked ? (
        <div className="gq-dw-chip gq-done"><BadgeCheckIcon /><span>Completed</span></div>
      ) : (
        <div className="gq-dw-chip">
          <img src={chest} alt="" />
          <span>{s.badge}</span>
        </div>
      )}
      <div className="gq-dw-title">{s.title}</div>
      <p className="gq-dw-desc">{s.text}</p>
      <div className="gq-dw-div" />
      <div className="gq-dw-prog">
        <div className="gq-dw-track">
          <div className="gq-dw-fill" style={{ transform: `scaleX(${(pos / 4).toFixed(4)})` }} />
        </div>
        {locked && <span className="gq-dw-knob" aria-hidden="true" />}
        {[1, 2, 3, 4].map((k) => {
          const lit = !locked && (s.completed || (Number.isFinite(s.amount) && s.amount >= (s.stepAmount || 500) * k));
          return (
            <img
              key={k}
              className={`gq-dw-diya gq-d${k}${lit ? ' gq-on' : ''}`}
              src={lit ? diyaLit : diya}
              alt=""
            />
          );
        })}
        <img className="gq-dw-chest" src={chest} alt="" />
        {(s.labels || []).map((label, k) => (
          <div key={label} className={`gq-dw-lab gq-l${k}`}>{label}</div>
        ))}
      </div>
      {locked ? (
        <>
          <p className="gq-dw-note">{lockCopy(s)}</p>
          <button type="button" className="gq-dw-btn" disabled>
            <LockBtnIcon />
            <span>Locked</span>
          </button>
        </>
      ) : s.completed ? (
        <button type="button" className="gq-dw-btn gq-done" disabled>
          <DoneCheckIcon />
          <span>Streak complete · Chest earned</span>
        </button>
      ) : (
        <button type="button" className="gq-dw-btn" onClick={() => onCta && onCta(s.action)}>
          <span>{s.cta}</span>
        </button>
      )}
      <span className="gq-dw-sr">{`₹${Math.round(s.amount || 0).toLocaleString('en-IN')} of ₹${run.toLocaleString('en-IN')}`}</span>
    </article>
  );
}

const UNITS = [
  ['days', 'days'],
  ['hrs', 'hrs'],
  ['mins', 'mins'],
  ['secs', 'secs'],
];

/* Festive block above Your Streaks. Cards whose amount has reached ₹2000
   are already ordered to the bottom by mapDiwaliBonus. */
export default function DiwaliBonus({ bonus, onCta }) {
  const left = useRemain(bonus.endsAt);
  return (
    <section className="gq-diwali" aria-label={bonus.title}>
      <div className="gq-dw-head">
        <h2 className="gq-dw-h">{bonus.title}</h2>
        <p className="gq-dw-sub">{bonus.text}</p>
        <div className="gq-dw-timer" role="timer" aria-label={`${left.days} days ${left.hrs} hours ${left.mins} minutes ${left.secs} seconds remaining`}>
          {UNITS.map(([key, label], i) => (
            <span key={key} className="gq-dw-unitwrap">
              {i > 0 && <span className="gq-dw-colon" aria-hidden="true">:</span>}
              <span className="gq-dw-unit">
                <span className="gq-dw-face">{pad(left[key])}</span>
                <span className="gq-dw-ulab">{label}</span>
              </span>
            </span>
          ))}
        </div>
      </div>
      {bonus.streaks.map((s) => (
        <DiwaliCard key={s.key} streak={s} onCta={onCta} />
      ))}
    </section>
  );
}
